import React, { useState } from "react";
import "../../day-plan-styles/TaskCompletionPrompt.css";
import { useTranslation } from "@preppa/translation";
//SKAPA Components
import SSRIcon from "@ingka/ssr-icon";
import Button from "@ingka/button";
import Text from "@ingka/text";
//SKAPA Styles
import "@ingka/hyperlink/dist/style.css";
import "@ingka/forms/dist/style.css";
//SKAPA Icons
import doneIcon from "@ingka/ssr-icon/paths/checkmark";
import {
  formatVolume,
  sanitizeDecimalInput,
} from "../../../../utils/planDataHelpers";

interface TaskCompletionPromptProps {
  initialProducedQty: number;
  uomCode: string;
  originStatus: string;
  totalPlannedVol: number;
  availableVol: number;
  conversionFactor?: number;
  onConfirm: (producedQty: number) => void;
  onCancel: () => void;
}

function TaskCompletionPrompt({
  initialProducedQty,
  uomCode,
  originStatus,
  totalPlannedVol,
  availableVol,
  conversionFactor = 1,
  onConfirm,
  onCancel,
}: TaskCompletionPromptProps) {
  const { translate } = useTranslation();
  const displayInitialProducedQty = initialProducedQty / conversionFactor;
  const displayTotalPlannedVol = totalPlannedVol / conversionFactor;
  const displayAvailableVol = availableVol / conversionFactor;

  const todoDefaultProducedQty = Math.max(
    displayTotalPlannedVol - displayAvailableVol,
    0,
  );

  const defaultProducedQty =
    originStatus === "todo"
      ? todoDefaultProducedQty
      : displayInitialProducedQty;

  const [producedQty, setProducedQty] = useState<string>(
    formatVolume(defaultProducedQty),
  );
  const [error, setError] = useState<string>("");
  const [validationWarning, setValidationWarning] = useState<string>("");

  const exceedsServerLimit = (value: string): boolean => {
    const num = parseFloat(value) || 0;
    return Math.floor(Math.abs(num * conversionFactor)) > 99999999;
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitized = sanitizeDecimalInput(rawValue);

    if (sanitized.length !== rawValue.length) {
      setValidationWarning(translate("dayPlan.prompts.onlyNumbers"));
    } else if (exceedsServerLimit(sanitized)) {
      setValidationWarning(translate("dayPlan.prompts.valueExceedsMax"));
      setError("");
      return;
    } else {
      setValidationWarning("");
    }
    setError("");
    setProducedQty(sanitized);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) return;
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter'];
    if (allowedKeys.includes(e.key)) return;
    const input = e.currentTarget;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const newValue = producedQty.substring(0, start) + e.key + producedQty.substring(end);
    const sanitized = sanitizeDecimalInput(newValue);
    if (sanitized.length !== newValue.length) {
      e.preventDefault();
      setValidationWarning(translate("dayPlan.prompts.onlyNumbers"));
      return;
    }
    if (exceedsServerLimit(sanitized)) {
      e.preventDefault();
      setValidationWarning(translate("dayPlan.prompts.valueExceedsMax"));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const input = e.currentTarget;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const rawValue = producedQty.substring(0, start) + text + producedQty.substring(end);
    const composed = sanitizeDecimalInput(rawValue);
    if (composed.length !== rawValue.length) {
      setValidationWarning(translate("dayPlan.prompts.onlyNumbers"));
    } else {
      setValidationWarning("");
    }
    if (exceedsServerLimit(composed)) {
      setValidationWarning(translate("dayPlan.prompts.valueExceedsMax"));
      return;
    }
    setProducedQty(composed);
  };

  const handleConfirm = () => {
    const numValue = parseFloat(producedQty) || 0;

    if (numValue < 0) {
      setError(translate("dayPlan.prompts.quantityMustBePositive"));
      return;
    }

    onConfirm(numValue * conversionFactor);
  };

  return (
    <div id="task-completion-prompt-wrapper">
      <div id="task-completion-prompt-header">
        <SSRIcon
          className="card-icon"
          paths={doneIcon}
          color="black"
          viewBox="2 2 20 20"
        />
        <span>{translate("dayPlan.prompts.moveToDone")}</span>
      </div>
      <div id="task-completion-prompt-content">
        <label id="produced-qty-input-label">{translate("dayPlan.prompts.producedQuantity")}</label>
        <div id="produced-qty-input-wrapper">
          <input
            id="produced-qty-input"
            type="text"
            inputMode="decimal"
            pattern="[0-9.,]*"
            value={producedQty}
            onChange={handleQuantityChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={() =>
              setProducedQty((prev) => formatVolume(parseFloat(prev) || 0))
            }
            autoFocus
          />
          <span id="uom-label">{uomCode === "each" ? "" : uomCode}</span>
        </div>
        <label className={`validation-error-label${validationWarning ? " visible" : ""}`}>
          {validationWarning || "\u00A0"}
        </label>
      </div>
      <div id="task-completion-prompt-button-container">
        <Button
          text={translate("dayPlan.prompts.ok")}
          type="emphasised"
          size="small"
          onClick={handleConfirm}
        />
        <Button
          text={translate("dayPlan.prompts.cancel")}
          type="secondary"
          size="small"
          onClick={onCancel}
        />
      </div>
    </div>
  );
}

export default TaskCompletionPrompt;
